import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Package, Calendar, Phone, MapPin, Hash, IdCard, Users as UsersIcon, Briefcase, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import ImagePreview from '../common/ImagePreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Small helper — open a base64 PDF in a new tab or force-download. */
const downloadOrOpenPdf = (dataUri, filename, mode = 'print') => {
  if (!dataUri) return;
  if (mode === 'download') {
    const a = document.createElement('a');
    a.href = dataUri; a.download = filename || 'receipt.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    return;
  }
  const w = window.open('', '_blank');
  if (w) w.document.write(`<iframe src="${dataUri}" style="width:100%;height:100%;border:0" onload="setTimeout(()=>this.contentWindow.print(),400)"></iframe>`);
};

/**
 * Read-only 360° detail sheet for a customer. Used from the "Upcoming
 * Installments" widget when the user taps a row. Shows:
 *   • Customer info (name, account #, CNIC, mobile, address, branch)
 *   • Sales Executive & any other assignees
 *   • Related agreement + product breakdown
 *   • Schedule (paid / pending / overdue rows)
 * Nothing here changes state — safe for any role that already has visibility
 * over the customer.
 */
export default function CustomerDetailsDialog({ open, onOpenChange, customerId, installmentId }) {
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [installment, setInstallment] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [err, setErr] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(false);
  // Inline installment payment
  const [payRow, setPayRow] = useState(null); // {installment_no, due_date, remaining}
  const [payForm, setPayForm] = useState({ amount: '', payment_method: 'Cash', notes: '', paid_date: new Date().toISOString().slice(0,10) });
  const [paying, setPaying] = useState(false);

  const reload = async (custId, agId) => {
    if (!custId) return;
    const [custRes, allInst, users] = await Promise.all([
      axios.get(`${API_URL}/api/customers/${custId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/installments?customer_id=${custId}`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    const cust = custRes.data;
    setCustomer(cust);
    const list = allInst.data || [];
    const ins = list.find(i => i.id === agId) || list[0] || null;
    setInstallment(ins);
    const ids = new Set([...(cust?.assigned_user_ids || []), cust?.assigned_user_id].filter(Boolean));
    setAssignees((users.data || []).filter(u => ids.has(u.id)));
    if (ins) {
      const [sched, inv] = await Promise.all([
        axios.get(`${API_URL}/api/installments/schedule/${ins.id}`, { withCredentials: true }).catch(() => ({ data: { schedule: [] } })),
        (ins.line_items?.length
          ? Promise.all(ins.line_items.map(li => axios.get(`${API_URL}/api/inventory/${li.inventory_id}`, { withCredentials: true }).then(r => r.data).catch(() => null)))
          : axios.get(`${API_URL}/api/inventory/${ins.inventory_id}`, { withCredentials: true }).then(r => [r.data]).catch(() => [])
        ),
      ]);
      setSchedule(sched.data?.schedule || []);
      setInventory((Array.isArray(inv) ? inv : []).filter(Boolean));
    }
  };

  const openPayForRow = (row) => {
    setPayRow(row);
    setPayForm({
      amount: row.remaining || row.amount_due || installment?.monthly_amount || 0,
      payment_method: 'Cash',
      notes: `Installment #${row.installment_no}`,
      paid_date: new Date().toISOString().slice(0, 10),
    });
  };

  const submitPay = async () => {
    if (!payRow || !installment) return;
    if (!Number(payForm.amount) || Number(payForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      setPaying(true);
      const { data } = await axios.post(
        `${API_URL}/api/installments/payment/${installment.id}`,
        {
          amount: Number(payForm.amount),
          payment_method: payForm.payment_method,
          notes: payForm.notes || null,
          paid_date: new Date(payForm.paid_date).toISOString(),
          installment_no: Number(payRow.installment_no),
          due_date: payRow.due_date,
        },
        { withCredentials: true },
      );
      toast.success(`Payment recorded — Receipt ${data.receipt_number}`);
      downloadOrOpenPdf(data.receipt_pdf, `Receipt_${data.receipt_number}.pdf`, 'print');
      setPayRow(null);
      // Refresh schedule + installment so the row flips to Paid live and
      // dashboard cascades pick up the change on next fetch.
      await reload(customerId, installment.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    } finally { setPaying(false); }
  };

  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true); setErr(null);
    setCustomer(null); setInstallment(null); setSchedule([]); setInventory([]); setAssignees([]); setPayRow(null);
    reload(customerId, installmentId)
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load details'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, installmentId]);

  const salesExec = assignees.find(u => u.role === 'Sales Executive' || u.role === 'Sales Officer');
  const others = assignees.filter(u => u.id !== salesExec?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-details-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <User className="h-5 w-5 text-amber-500" /> {customer?.name || 'Customer'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {customer?.account_number ? `Account ${customer.account_number}` : 'Loading…'}
            {customer?.approval_status && <span className="ml-2 uppercase text-xs text-emerald-500">· {customer.approval_status}</span>}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-slate-400">Loading details…</div>}
        {err && <div className="py-4 text-center text-red-400" data-testid="details-error">{err}</div>}

        {!loading && customer && (
          <div className="space-y-4">
            {/* Passport photo + Customer & Sales-Exec block */}
            <div className="grid md:grid-cols-[130px_1fr_1fr] gap-4 text-sm">
              {/* Passport-size customer photo (35 × 45 mm at ~72dpi ≈ 130×166) */}
              <div className="flex flex-col items-center" data-testid="details-customer-photo-wrap">
                {customer.photo_url ? (
                  <button type="button" onClick={() => setPhotoPreview(true)}
                    data-testid="details-customer-photo"
                    title="Click to enlarge"
                    className="rounded-sm overflow-hidden border-2 border-amber-500/40 hover:border-amber-500 transition bg-white"
                    style={{ width: 130, height: 166 }}>
                    <img src={customer.photo_url} alt={customer.name}
                      className="w-full h-full object-cover" />
                  </button>
                ) : (
                  <div className="rounded-sm border-2 border-slate-700 bg-slate-800 flex items-center justify-center text-slate-500"
                    style={{ width: 130, height: 166 }} data-testid="details-customer-photo-placeholder">
                    <User className="h-14 w-14" />
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1.5">Passport Photo</div>
              </div>
              <div className="bg-slate-800/50 rounded-sm p-3 space-y-1 border border-slate-800">
                <div className="text-slate-500 text-xs uppercase mb-1">Customer</div>
                <div className="flex items-center gap-2"><IdCard className="h-3 w-3 text-slate-500" /><span className="text-slate-400">CNIC:</span><span className="text-white">{customer.cnic || '—'}</span></div>
                <div className="flex items-center gap-2"><Phone className="h-3 w-3 text-slate-500" /><span className="text-slate-400">Mobile:</span><span className="text-white">{customer.mobile || '—'}</span></div>
                <div className="flex items-center gap-2"><MapPin className="h-3 w-3 text-slate-500" /><span className="text-slate-400">Address:</span><span className="text-white text-xs">{customer.address || '—'}</span></div>
                <div className="flex items-center gap-2"><Hash className="h-3 w-3 text-slate-500" /><span className="text-slate-400">Branch:</span><span className="text-white text-xs">{customer.branch_id ? customer.branch_id.slice(-6) : '—'}</span></div>
              </div>
              <div className="bg-slate-800/50 rounded-sm p-3 space-y-2 border border-slate-800">
                <div className="text-slate-500 text-xs uppercase mb-1">Assigned Users</div>
                <div className="flex items-center gap-2" data-testid="details-sales-executive">
                  <Briefcase className="h-3 w-3 text-emerald-500" />
                  <span className="text-slate-400">Sales Executive:</span>
                  <span className="text-emerald-400 font-semibold">{salesExec?.name || '— Not assigned —'}</span>
                </div>
                {customer?.server_name && (
                  <div className="flex items-center gap-2" data-testid="details-server-name">
                    <UsersIcon className="h-3 w-3 text-cyan-400" />
                    <span className="text-slate-400">Server:</span>
                    <span className="text-cyan-300 font-medium">{customer.server_name}</span>
                  </div>
                )}
                {others.length > 0 && (
                  <div className="flex items-start gap-2">
                    <UsersIcon className="h-3 w-3 text-cyan-400 mt-0.5" />
                    <div>
                      <div className="text-slate-400">Other Assignees</div>
                      {others.map(u => (
                        <div key={u.id} className="text-cyan-300 text-xs">{u.name} <span className="text-slate-500">({u.role})</span></div>
                      ))}
                    </div>
                  </div>
                )}
                {assignees.length === 0 && <div className="text-slate-500 text-xs">No user assigned yet.</div>}
              </div>
            </div>

            {/* Agreement summary */}
            {installment && (
              <div className="bg-slate-800/50 rounded-sm p-3 border border-slate-800 space-y-1 text-sm" data-testid="details-agreement">
                <div className="text-slate-500 text-xs uppercase mb-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> Agreement</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                  <div><span className="text-slate-400">Agreement #:</span> <span className="text-white">{installment.agreement_number || '—'}</span></div>
                  <div><span className="text-slate-400">Plan:</span> <span className="text-white">{installment.plan_name || installment.plan_type || '—'}</span></div>
                  <div><span className="text-slate-400">Months:</span> <span className="text-white">{installment.plan_months || '—'}</span></div>
                  <div><span className="text-slate-400">Total:</span> <span className="text-amber-500">PKR {installment.total_amount?.toLocaleString()}</span></div>
                  <div><span className="text-slate-400">Down:</span> <span className="text-cyan-400">PKR {installment.down_payment?.toLocaleString()}</span></div>
                  <div><span className="text-slate-400">Monthly:</span> <span className="text-white">PKR {installment.monthly_amount?.toLocaleString()}</span></div>
                  <div><span className="text-slate-400">Paid:</span> <span className="text-emerald-500">PKR {installment.paid_amount?.toLocaleString()}</span></div>
                  <div><span className="text-slate-400">Remaining:</span> <span className="text-yellow-500">PKR {installment.remaining_amount?.toLocaleString()}</span></div>
                  <div><span className="text-slate-400">Status:</span> <span className="text-white capitalize">{installment.status || '—'}</span></div>
                </div>
              </div>
            )}

            {/* Products */}
            {inventory.length > 0 && (
              <div className="bg-slate-800/50 rounded-sm p-3 border border-slate-800 space-y-1 text-sm" data-testid="details-products">
                <div className="text-slate-500 text-xs uppercase mb-1 flex items-center gap-1"><Package className="h-3 w-3" /> Product(s)</div>
                {inventory.map((p) => (
                  <div key={p.id} className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 border-t border-slate-800 pt-1">
                    <div><span className="text-slate-400">Item:</span> <span className="text-white">{p.brand} {p.model}</span></div>
                    <div><span className="text-slate-400">Type:</span> <span className="text-white">{p.product_type}</span></div>
                    <div><span className="text-slate-400">IMEI/Serial:</span> <span className="text-white text-xs">{p.imei || p.serial_number || '—'}</span></div>
                    <div><span className="text-slate-400">Sale Price:</span> <span className="text-amber-500">PKR {p.sale_price?.toLocaleString()}</span></div>
                  </div>
                ))}
              </div>
            )}

            {/* Schedule */}
            {schedule.length > 0 && (
              <div className="bg-slate-800/50 rounded-sm p-3 border border-slate-800" data-testid="details-schedule">
                <div className="text-slate-500 text-xs uppercase mb-2">Schedule</div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800">
                        <TableHead className="text-slate-400 text-xs">#</TableHead>
                        <TableHead className="text-slate-400 text-xs">Due Date</TableHead>
                        <TableHead className="text-slate-400 text-xs">Amount</TableHead>
                        <TableHead className="text-slate-400 text-xs">Paid</TableHead>
                        <TableHead className="text-slate-400 text-xs">Remaining</TableHead>
                        <TableHead className="text-slate-400 text-xs">Status</TableHead>
                        <TableHead className="text-slate-400 text-xs text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schedule.map((r) => {
                        const isPaid = r.status === 'paid';
                        return (
                        <TableRow key={r.installment_no} className="border-slate-800">
                          <TableCell className="text-white text-xs">{r.installment_no}</TableCell>
                          <TableCell className="text-slate-300 text-xs">{new Date(r.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                          <TableCell className="text-slate-300 text-xs">PKR {r.amount_due?.toLocaleString()}</TableCell>
                          <TableCell className="text-emerald-500 text-xs">PKR {r.paid_amount?.toLocaleString()}</TableCell>
                          <TableCell className="text-amber-500 text-xs">PKR {r.remaining?.toLocaleString()}</TableCell>
                          <TableCell><span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase ${
                            r.status === 'paid' ? 'bg-emerald-500/20 text-emerald-500' :
                            r.status === 'overdue' ? 'bg-red-500/20 text-red-500' :
                            'bg-slate-700/50 text-slate-400'
                          }`}>{r.status}</span></TableCell>
                          <TableCell className="text-right">
                            {isPaid ? (
                              <span className="text-slate-600 text-xs">—</span>
                            ) : (
                              <Button size="sm"
                                onClick={() => openPayForRow(r)}
                                data-testid={`details-pay-${r.installment_no}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs"
                                title={`Pay installment #${r.installment_no}`}>
                                <DollarSign className="h-3 w-3 mr-1" /> Pay
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );})}
                    </TableBody>
                  </Table>
                </div>

                {/* Inline payment form — appears when a Pay button is clicked */}
                {payRow && installment && (
                  <div className="mt-3 border border-emerald-500/40 bg-emerald-950/20 rounded-sm p-3" data-testid="details-payment-form">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-emerald-400">
                        Pay Installment #{payRow.installment_no}
                        <span className="text-xs text-slate-400 ml-2">— Due {new Date(payRow.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      </div>
                      <button type="button" onClick={() => setPayRow(null)}
                        data-testid="details-payment-cancel"
                        className="text-slate-400 hover:text-red-400 text-xs">Cancel</button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-xs">Amount (PKR)</Label>
                        <Input type="number" value={payForm.amount}
                          onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                          data-testid="details-payment-amount"
                          className="bg-slate-800 border-slate-700 text-white h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-xs">Method</Label>
                        <Select value={payForm.payment_method}
                          onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm" data-testid="details-payment-method">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700 text-white">
                            {['Cash', 'JazzCash', 'EasyPaisa', 'Bank Transfer', 'Cheque'].map((m) => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-xs">Receiving Date</Label>
                        <Input type="date" value={payForm.paid_date}
                          onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })}
                          data-testid="details-payment-date"
                          className="bg-slate-800 border-slate-700 text-white h-8 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-xs">Notes</Label>
                        <Input value={payForm.notes}
                          onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                          data-testid="details-payment-notes"
                          className="bg-slate-800 border-slate-700 text-white h-8 text-sm" />
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <Button size="sm" onClick={submitPay} disabled={paying}
                        data-testid="details-payment-submit"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        {paying ? 'Recording…' : 'Confirm Payment'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Full-size photo lightbox */}
        <ImagePreview
          open={photoPreview}
          onOpenChange={setPhotoPreview}
          src={customer?.photo_url}
          title={`${customer?.name || ''} — Photo`}
        />
      </DialogContent>
    </Dialog>
  );
}
